import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ui from 'domains/ui';
import { ShufflerContext } from './Shuffler';

import type { ShufflerContextType } from './Shuffler';
import type { ShufflerFilterGroup, ShufflerFilterItem } from './data/filters.types';
import type { StrictUnion } from 'types/common_types';

type ShufflerFilterGroupProps = {
  group: string;
  label?: string;
  filters: StrictUnion<ShufflerFilterItem[] | number[] | boolean[]>;
  type?: string;
  metakey?: string;
};

function filterIsFilterItem(f: ShufflerFilterGroupProps['filters'][0]): f is ShufflerFilterItem {
  return typeof f !== 'boolean' && typeof f !== 'number';
}

function ShufflerFilterGroup(props: ShufflerFilterGroupProps) {
  const { group, label, filters, type, metakey } = props;
  const hasMobileNavigation = useSelector(ui.selectors.hasMobileNavigation);
  const { byFilter, metalabels, selectedFilters, filterCollapse, collapseFilters } = useContext(
    ShufflerContext
  ) as ShufflerContextType;

  // temporary
  const filterArray = useMemo(() => {
    if (filterIsFilterItem(filters[0])) {
      const typed = filters as ShufflerFilterItem[];
      return typed.map((f) => f.name);
    }

    return filters as number[] | boolean[];
  }, [filters]);

  const itemId = (name: string | number | boolean) => {
    const p = [name];
    if (metakey) {
      p.unshift(metakey);
    }
    if (type) {
      p.unshift(type);
    }
    return p.join('-');
  };

  const groupName = (name: string) => {
    const metalabel = name.split('--').pop();
    return type === 'meta' && metalabel ? metalabels[metalabel] : name;
  };

  const itemMeta = () => {
    return type === 'meta' ? group : undefined;
  };

  const itemGroup = () => {
    return group || undefined;
  };

  const itemName = (name: string | number | boolean) => {
    return name === true ? 'Yes!' : name.toString();
  };

  const iconClass = !collapseFilters.has(groupName(group).toLowerCase())
    ? 'chevron-down'
    : 'chevron-up';

  return (
    <React.Fragment>
      <strong>
        <FontAwesomeIcon
          icon={iconClass}
          data-groupname={groupName(group)}
          onClick={filterCollapse}
        />
        {label || groupName(group)}
      </strong>
      <div className="group_filters">
        {!collapseFilters.has(groupName(group).toLowerCase()) &&
          filterArray.length > 0 &&
          filterArray.map((name) => (
            <React.Fragment key={itemId(name)}>
              {hasMobileNavigation && (
                <button
                  id={itemId(name)}
                  value={name.toString()}
                  data-groupname={itemGroup()}
                  data-meta={itemMeta()}
                  onClick={(e) => byFilter(e.target)}
                >
                  {itemName(name)}
                </button>
              )}
              {!hasMobileNavigation && (
                <div className="category_filter">
                  <input
                    type="checkbox"
                    id={itemId(name)}
                    value={name.toString()}
                    data-groupname={itemGroup()}
                    data-meta={itemMeta()}
                    onChange={(e) => byFilter(e.target)}
                    checked={[...selectedFilters].indexOf(`${name}:${itemGroup()}`) >= 0}
                  />
                  <label htmlFor={itemId(name)}>{itemName(name)}</label>
                </div>
              )}
            </React.Fragment>
          ))}
      </div>
    </React.Fragment>
  );
}

export default ShufflerFilterGroup;
